// DealLostReasonDialog — обязательный выбор причины перед POST сделки в LOST (V16 W3).

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { rejectionReasonsApi } from '@/api/rejectionReasons';
import { t } from '@/i18n';

/** Пропсы диалога причины отказа при переходе сделки в LOST. */
export interface DealLostReasonDialogProps {
  /** Открыт ли диалог. */
  open: boolean;
  /** Закрытие без POST (отмена). */
  onClose: () => void;
  /** Подтверждение: выбранный id причины; POST делает владелец (DealDetailPage). */
  onConfirm: (rejectionReasonId: string) => void;
  /** Мутация стадии в полёте — блокирует повторный submit. */
  pending?: boolean;
}

/**
 * Диалог выбора причины отказа перед переводом сделки в LOST.
 * Каталог — GET /api/rejection-reasons; без OK POST не уходит.
 */
export function DealLostReasonDialog({
  open,
  onClose,
  onConfirm,
  pending = false,
}: DealLostReasonDialogProps): JSX.Element {
  const [reasonId, setReasonId] = useState('');
  const catalogQuery = useQuery({
    queryKey: ['rejection-reasons'],
    queryFn: () => rejectionReasonsApi.list(),
    enabled: open,
    retry: false,
  });
  const reasons = catalogQuery.data ?? [];

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="deal-lost-reason-title">
      <DialogTitle id="deal-lost-reason-title">{t('crm.deals.lostReason.title')}</DialogTitle>
      <DialogContent>
        {catalogQuery.isError ? (
          <Alert severity="error">{t('crm.deals.lostReason.loadError')}</Alert>
        ) : (
          <TextField
            select
            fullWidth
            required
            margin="dense"
            label={t('crm.deals.lostReason.label')}
            value={reasonId}
            onChange={(e) => setReasonId(e.target.value)}
            disabled={catalogQuery.isLoading || pending}
            inputProps={{ 'aria-label': t('crm.deals.lostReason.label') }}
          >
            {reasons.map((reason) => (
              <MenuItem key={reason.id} value={reason.id}>
                {reason.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={!reasonId || pending}
          onClick={() => onConfirm(reasonId)}
        >
          {t('crm.deals.lostReason.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
